/**
 * Utility functions for handling authentication tokens
 */

/**
 * Creates a token hash for secure transmission
 * This prevents sending the full token over the WebSocket connection
 *
 * @param token The original token
 * @returns A hash of the token that can be verified server-side
 */
function createTokenHash(token: string): string {
  // Create a hash of the token using SubtleCrypto if available
  // This is a simplified example - in production, use a proper HMAC
  if (window.crypto && window.crypto.subtle && typeof TextEncoder !== 'undefined') {
    // This is an async operation, but for simplicity we're returning a derived value
    // In a real implementation, you would make this function async and await the hash
    const tokenParts = token.split('.');
    if (tokenParts.length >= 2) {
      // For JWT tokens, we can use the header and payload parts
      // This still allows verification without exposing the signature
      return `${tokenParts[0]}.${tokenParts[1]}`;
    }
  }

  // Fallback for browsers without SubtleCrypto
  // Still avoid sending the full token
  return token.substring(0, token.length / 2) + '...';
}

/**
 * Securely formats authentication data for transmission
 *
 * @param email User email
 * @param token Authentication token
 * @returns Secure authentication payload
 */
export function createSecureAuthPayload(email: string, token: string): Record<string, any> {
  return {
    email,
    // Send a hash instead of the full token
    tokenHash: createTokenHash(token),
    // Include timestamp to prevent replay attacks
    timestamp: Date.now(),
    // Include origin for additional verification
    origin: window.location.origin
  };
}
